import {
  GOODS_LOW_STOCK_PERCENT,
  type YearEndGoodsRegionEdition,
} from '@/api/getYearEndGoodsInstitutionList';

import { WELLO_IMAGE_PREFIX_URL } from './url';

//* 연말 이벤트 한정 — 되돌리는 절차는 src/plans/WEL-5478-donation-home-entry.md
export const YEAR_END_GOODS_STOCK_STATUS = {
  AVAILABLE: 'AVAILABLE',
  CLOSING: 'CLOSING',
  SOLD_OUT: 'SOLD_OUT',
} as const;

export type YearEndGoodsStockStatus =
  (typeof YEAR_END_GOODS_STOCK_STATUS)[keyof typeof YEAR_END_GOODS_STOCK_STATUS];

export const getYearEndGoodsGift = (
  edition: YearEndGoodsRegionEdition | null | undefined,
) => {
  if (!edition) {
    return null;
  }

  //* 기간 종료는 이벤트 시작 전(edition null)과 같이 굿즈 요소를 모두 숨긴다
  if (edition.closedReason === 'PERIOD_ENDED') {
    return null;
  }

  const { minDonationAmount } = edition;

  //* 수량 소진 마감은 남은 비율 0으로 그린다
  if (edition.status === 'CLOSED') {
    return {
      remainingPercent: 0,
      stockStatus: YEAR_END_GOODS_STOCK_STATUS.SOLD_OUT,
      minDonationAmount,
    };
  }

  return {
    remainingPercent: edition.remainingPercent,
    stockStatus:
      edition.remainingPercent <= GOODS_LOW_STOCK_PERCENT
        ? YEAR_END_GOODS_STOCK_STATUS.CLOSING
        : YEAR_END_GOODS_STOCK_STATUS.AVAILABLE,
    minDonationAmount,
  };
};

//* 기획 고정 발송 시작일 — 화면마다 표기가 달라(12.22 · 12월 22일) 사용처에서 포맷한다
export const YEAR_END_DIARY_SHIP_START_DATE = '2026-12-22';

export const YEAR_END_NAVER_PAY_IMAGE_URL = `${WELLO_IMAGE_PREFIX_URL}/static/event/localfesta/naver-pay.png`;

export type YearEndGoodsGift = NonNullable<
  ReturnType<typeof getYearEndGoodsGift>
>;

export const YEAR_END_GIFT_OPTION = {
  DIARY: 'DIARY',
  POINT: 'POINT',
} as const;

export type YearEndGiftOption =
  (typeof YEAR_END_GIFT_OPTION)[keyof typeof YEAR_END_GIFT_OPTION];

export const YEAR_END_GIFT_REQUIRED = '연말선물을 선택해주세요';

export const YEAR_END_GIFT_SELECT_ID = 'year-end-gift-select';

export const YEAR_END_DELIVERY_CONSENT_REQUIRED = '굿즈 배송 동의가 필요해요';

export const YEAR_END_DELIVERY_CONSENT_ID = 'year-end-delivery-consent';

export const YEAR_END_GIFT_SECTION = {
  HIDDEN: 'HIDDEN',
  SOLD_OUT: 'SOLD_OUT',
  SELECTABLE: 'SELECTABLE',
} as const;

export type YearEndGiftSection =
  (typeof YEAR_END_GIFT_SECTION)[keyof typeof YEAR_END_GIFT_SECTION];

//* 최소 기부 금액 미만이 소진보다 먼저다 — 기획상 금액 미만이면 마감 안내도 띄우지 않는다
export const getYearEndGiftSection = ({
  goodsGift,
  donationAmount,
}: {
  goodsGift: YearEndGoodsGift | null;
  donationAmount: number;
}): YearEndGiftSection => {
  if (!goodsGift || donationAmount < goodsGift.minDonationAmount) {
    return YEAR_END_GIFT_SECTION.HIDDEN;
  }

  if (goodsGift.stockStatus === YEAR_END_GOODS_STOCK_STATUS.SOLD_OUT) {
    return YEAR_END_GIFT_SECTION.SOLD_OUT;
  }

  return YEAR_END_GIFT_SECTION.SELECTABLE;
};

export const getYearEndGiftBlockedReason = ({
  section,
  option,
  isDeliveryConsented,
}: {
  section: YearEndGiftSection;
  option: YearEndGiftOption | null;
  isDeliveryConsented: boolean;
}) => {
  if (section !== YEAR_END_GIFT_SECTION.SELECTABLE) {
    return null;
  }

  if (!option) {
    return YEAR_END_GIFT_REQUIRED;
  }

  if (option === YEAR_END_GIFT_OPTION.DIARY && !isDeliveryConsented) {
    return YEAR_END_DELIVERY_CONSENT_REQUIRED;
  }

  return null;
};

export const isYearEndDiaryChosen = ({
  section,
  option,
}: {
  section: YearEndGiftSection;
  option: YearEndGiftOption | null;
}) =>
  section === YEAR_END_GIFT_SECTION.SELECTABLE &&
  option === YEAR_END_GIFT_OPTION.DIARY;
